import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  Divider,
  Grid,
  Button,
  Stack,
  Paper,
  TextField
} from '@mui/material';
import { format } from 'date-fns';
import PrintIcon from "@mui/icons-material/Print";
const HostelerInvoice = ({ open, onClose, hosteler }) => {
  if (!hosteler) return null;

  // ✅ Map API fields correctly
  const {
    hostelName,
    hostelerName,
    billingMonth,
    billingPeriodStartDate,
    billingPeriodEndDate,
    invoiceDate,
    invoiceNumber,
    rentFinalAmount = 0,
    mealFinalAmount = 0,
    previousDueAmount = 0,
    ownerConcessionBillAmount = 0,
    advancePaymentAmount = 0,
    rentDeductionForHostelClosure = 0,
    rentDeductionForStudentAbsent = 0,
    leaveRequestFoodDeduction = 0,
    closureHolidayFoodDeduction = 0,
    invoiceTotalAmount = 0,
    paidAmount = 0,
    paymentStatus = 'NOT_STARTED',
    paymentDueDate,
	depositAmount,
    invoiceExtraChargeDTOs,
    absentDateList
  } = hosteler;

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);

  const formatDate = (dateStr) =>
    dateStr ? format(new Date(dateStr), 'dd MMM yyyy') : '-';
	
	const generateInvoiceText = () => {
  const pendingAmount =
    Number(invoiceTotalAmount || 0) -
    Number(paidAmount || 0);

  const extraChargeTotalAmount =
    (invoiceExtraChargeDTOs || []).reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0
    );

  const extraChargeLines =
    (invoiceExtraChargeDTOs || []).length > 0
      ? invoiceExtraChargeDTOs
          .map(
            (item) =>
              `${item.itemName || "Extra Charge"} : ₹ ${Number(
                item.amount || 0
              ).toFixed(2)}`
          )
          .join("\n")
      : "None";

  return `
HOSTEL & PG INVOICE
================================

Invoice No : ${invoiceNumber || "-"}
Bill Month : ${billingMonth || "-"}
Bill Date  : ${formatDate(invoiceDate)}

Customer   : ${hostelerName || "-"}
Hostel     : ${hostelName || "-"}

--------------------------------
CHARGE DETAILS
--------------------------------

Rent Charges      ₹ ${Number(rentFinalAmount || 0).toFixed(2)}
Meal Charges      ₹ ${Number(mealFinalAmount || 0).toFixed(2)}
Extra Charges     ₹ ${Number(extraChargeTotalAmount).toFixed(2)}
Previous Due      ₹ ${Number(previousDueAmount || 0).toFixed(2)}
Security Deposit  ₹ ${Number(depositAmount || 0).toFixed(2)}

--------------------------------
EXTRA CHARGES
--------------------------------

${extraChargeLines}

--------------------------------
PAYMENT SUMMARY
--------------------------------

Total Bill        ₹ ${Number(invoiceTotalAmount || 0).toFixed(2)}
Paid Amount       ₹ ${Number(paidAmount || 0).toFixed(2)}
Pending Amount    ₹ ${Number(pendingAmount).toFixed(2)}

Status            ${paymentStatus || "NOT_STARTED"}

================================

Thank You For Staying With Us

Website : https://hostelandpg.com
`;
};

const handlePrint = () => {
  const content = generateInvoiceText().replace(/\n/g, "<br/>");

  const win = window.open("", "", "width=800,height=600");

  win.document.write(`
    <html>
      <head>
        <title>Hosteler Invoice</title>
        <style>
          body {
            font-family: Arial;
            padding: 20px;
            line-height: 1.6;
          }
        </style>
      </head>
      <body>
        ${content}
      </body>
    </html>
  `);

  win.document.close();
  win.print();
};

  // ✅ Calculate derived values
  const totalDeductions =
    Number(leaveRequestFoodDeduction) +
    Number(closureHolidayFoodDeduction) +
    Number(ownerConcessionBillAmount) +
    Number(advancePaymentAmount) +
    Number(rentDeductionForHostelClosure) +
    Number(rentDeductionForStudentAbsent);

  const finalAmount = Number(invoiceTotalAmount);
  const pendingAmount = finalAmount - Number(paidAmount || 0);

  const SectionHeader = ({ title }) => (
    <Box
      sx={{
        backgroundColor: '#f5f5f5',
        px: 2,
        py: 1,
        borderRadius: 1,
        mt: 2,
        mb: 1
      }}
    >
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
    </Box>
  );

  const renderRow = (label, value) => (
    <Grid container sx={{ py: 0.5 }}>
      <Grid item xs={6}>
        <Typography>{label}</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography textAlign="right">{value}</Typography>
      </Grid>
    </Grid>
  );

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box textAlign="center" mb={2}>
          <Typography variant="h5" fontWeight="bold">
            Hostel Invoice
          </Typography>
          <Typography variant="subtitle2" color="textSecondary">
            {hostelName}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Invoice No: {invoiceNumber}
          </Typography>
        </Box>

        {/* 🔹 Added Read-only Fields: Hosteler Name + Billing Month */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Hosteler Name"
              value={hostelerName || '-'}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Billing Month"
              value={billingMonth || '-'}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>

        <Divider />

        {/* Hosteler Info */}
        <SectionHeader title="Hosteler Details" />
        {renderRow('Billing Start', formatDate(billingPeriodStartDate))}
        {renderRow('Billing End', formatDate(billingPeriodEndDate))}
        {renderRow('Invoice Date', formatDate(invoiceDate))}

        {/* Charges */}
        <SectionHeader title="Charges" />
        {renderRow('Rent Charges', formatCurrency(rentFinalAmount))}
        {renderRow('Meal Charges', formatCurrency(mealFinalAmount))}
        {renderRow('Previous Due', formatCurrency(previousDueAmount))}
		{renderRow('Security Deposit', formatCurrency(depositAmount))}

        {/* Extra Charges */}
        {invoiceExtraChargeDTOs && invoiceExtraChargeDTOs.length > 0 && (
          <>
            <SectionHeader title="Extra Charges" />
            {invoiceExtraChargeDTOs.map((item, idx) =>
              renderRow(
                item.itemName || `Extra Charge ${idx + 1}`,
                formatCurrency(item.amount)
              )
            )}
          </>
        )}

        {/* Deductions */}
        <SectionHeader title="Deductions" />
        {renderRow('Leave Food Deduction', formatCurrency(leaveRequestFoodDeduction))}
        {renderRow('Holiday Food Deduction', formatCurrency(closureHolidayFoodDeduction))}
        {renderRow('Owner Concession', formatCurrency(ownerConcessionBillAmount))}
        {renderRow('Advance Payment', formatCurrency(advancePaymentAmount))}
        {renderRow('Rent Deduction (Hostel Closure)', formatCurrency(rentDeductionForHostelClosure))}
        {renderRow('Rent Deduction (Student Absent)', formatCurrency(rentDeductionForStudentAbsent))}
        {renderRow('Total Deductions', formatCurrency(totalDeductions))}

        {/* Absent Dates */}
        <SectionHeader title="Absent Dates" />
<Typography>
  {Array.isArray(absentDateList)
    ? absentDateList.length > 0
      ? absentDateList.join(', ')
      : 'None'
    : typeof absentDateList === 'string'
    ? absentDateList
    : 'None'}
</Typography>


        {/* Payment Info */}
        <SectionHeader title="Payment Info" />
        {renderRow('Payment Status', paymentStatus)}
        {renderRow('Amount Paid', formatCurrency(paidAmount))}
        {renderRow('Pending Amount', formatCurrency(pendingAmount))}
        {renderRow('Payment Due Date', formatDate(paymentDueDate))}
		{renderRow('Deposit Amount', formatCurrency(depositAmount))}
        {/* Final */}
        <SectionHeader title="Final Amount Payable" />
        <Typography variant="h6" fontWeight="bold" textAlign="right">
          {formatCurrency(finalAmount)}
        </Typography>

        <Typography variant="body2" color="textSecondary" textAlign="right">
          Generated on: {formatDate(invoiceDate)}
        </Typography>

        {/* Close Button */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
  <Button
    variant="outlined"
    startIcon={<PrintIcon />}
    onClick={handlePrint}
  >
    Print
  </Button>

  <Button
    variant="contained"
    onClick={onClose}
  >
    Close
  </Button>
</Stack>
      </Paper>
    </Dialog>
  );
};

export default HostelerInvoice;
